import React, {useCallback} from 'react';

import {useAppDispatch} from '../../helpers/reduxHelpers';
import {removePanelAction} from '../../store/panels/panelsActions';
import {PanelState} from '../../store/panels/panelsState';
import {PanelTypesEnum} from '../../store/panels/panelStateBase';
import {TimelineVariationsEnum} from '../../store/timelineVariations/timelineVariationSelectors';
import {PopupAccountDetails} from './popupAccountDetails';
import {PopupApplications} from './popupApplications';
import {PopupCalendarViewer} from './popupCalendarViewer';
import {PopupComposerContainer} from './popupComposerContainer';
import {PopupContactDetails} from './popupContactDetails';
import {PopupCrmPreview} from './popupCrmPreview';
import {PopupEmlContainer} from './popupEmlContainer';
import {PopupMessageSource} from './popupMessageSource';
import {PopupMessageViewerContainer} from './popupMessageViewerContainer';
import {PopupViewerContainer} from './popupViewerContainer';

/*
 * Props.
 */

interface PopupProps {
  panel: PanelState;
}

/*
 * Constants.
 */

const popupTimelineVariation = TimelineVariationsEnum.ASYNCHRONOUS;

/*
 * Component.
 */

export const Popup = (props: PopupProps) => {
  const {panel} = props;

  const dispatch = useAppDispatch();
  const onRequestClose = useCallback(() => {
    dispatch(removePanelAction(panel.id));
  }, [dispatch, panel.id]);

  // Otherwise, render by type.
  switch (panel.type) {
    case PanelTypesEnum.COMPOSER:
      return (
        <PopupComposerContainer
          panelId={panel.id}
          {...panel}
          onRequestClose={onRequestClose}
          timelineVariation={popupTimelineVariation}
        />
      );

    case PanelTypesEnum.VIEWER:
      return <PopupViewerContainer panelId={panel.id} {...panel} onRequestClose={onRequestClose} />;

    case PanelTypesEnum.MESSAGE:
      return (
        <PopupMessageViewerContainer
          panelId={panel.id}
          {...panel}
          timelineVariation={popupTimelineVariation}
          onRequestClose={onRequestClose}
        />
      );

    case PanelTypesEnum.CALENDARS:
      return <PopupCalendarViewer panelId={panel.id} {...panel} onRequestClose={onRequestClose} />;

    case PanelTypesEnum.EML:
      return (
        <PopupEmlContainer
          panelId={panel.id}
          {...panel}
          timelineVariation={popupTimelineVariation}
          onRequestClose={onRequestClose}
        />
      );

    case PanelTypesEnum.CONTACT_DETAILS:
      return <PopupContactDetails panelId={panel.id} {...panel} onRequestClose={onRequestClose} />;

    case PanelTypesEnum.ACCOUNT_DETAILS:
      return <PopupAccountDetails panelId={panel.id} {...panel} onRequestClose={onRequestClose} />;

    case PanelTypesEnum.CRM_PREVIEW:
      return <PopupCrmPreview panelId={panel.id} {...panel} onRequestClose={onRequestClose} />;

    case PanelTypesEnum.APPLICATIONS:
      return <PopupApplications panelId={panel.id} {...panel} onRequestClose={onRequestClose} />;

    case PanelTypesEnum.MESSAGE_SOURCE:
      return <PopupMessageSource panelId={panel.id} {...panel} onRequestClose={onRequestClose} />;

    default:
      throw new Error('This panel type does not support popups.');
  }
};
